import { Button } from '@/components/ui/button';
import { SEOProps } from '@/components/ui/SEO';
import { Tag } from '@/components/ui/tag';
import { UnderlinedText } from '@/components/ui/underline-text';
import { MOTION } from '@/utils';
import { basePath, withBasePath } from '@/utils/basePath';
import {
  STATUTS_LIBRES,
  STATUTS_OBLIGATOIRES,
  STATUTS_RECOMMANDES,
  SUMMARY_ITEMS,
} from '@/utils/data/facturationContent';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText } from 'gsap/SplitText';
import Image from 'next/image';
import { useRef, useState } from 'react';

gsap.registerPlugin(ScrollTrigger, SplitText);

const FACTURATION_ELECTRONIQUE_SEO: SEOProps = {
  title: 'Facturation électronique 2026 : obligations et calendrier | zLawyer',
  description:
    "Comprenez la réforme de la facturation électronique obligatoire dès 2026 : e-invoicing, e-reporting, formats Factur-X, obligations et calendrier pour les cabinets d'avocats.",
  keywords:
    'facturation électronique, réforme 2026, e-invoicing, e-reporting, Factur-X, cabinet avocat, obligations TVA',
  url: `https://www.zlawyer.fr${basePath}/facturation-electronique/`,
};

type Statut = {
  nom: string;
  role: string;
  declencheur: string;
  utilite: string;
};

// ─── Sous-composants ──────────────────────────────────────────────────────────

function SummaryLink({ active, href, label }: { active: boolean; href: string; label: string }) {
  return (
    <a
      href={href}
      className={`block py-1.5 text-sm transition-colors duration-200 ${
        active ? 'text-orange font-roboto-medium' : 'text-dark-blue/50 hover:text-orange'
      }`}
    >
      {label}
    </a>
  );
}

function BulletItem({ children }: { children: React.ReactNode }) {
  return (
    <li className="text-dark-blue/70 flex items-start gap-3 leading-relaxed">
      <span className="text-orange mt-1 shrink-0">•</span>
      <span>{children}</span>
    </li>
  );
}

function StatusSection({
  title,
  items,
  description,
}: {
  title: string;
  items: Statut[];
  description: string;
}) {
  return (
    <div className="mb-12">
      <h4 className="text-orange font-roboto-medium mb-2 text-base">{title}</h4>
      <p className="text-dark-blue/60 mb-5 text-sm">{description}</p>

      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-xl md:block">
        <table className="min-w-full border-collapse">
          <thead>
            <tr className="bg-dark-blue/3">
              <th className="text-dark-blue/50 px-4 py-3 text-left text-xs font-semibold tracking-wider uppercase">
                Statut
              </th>
              <th className="text-dark-blue/50 px-4 py-3 text-left text-xs font-semibold tracking-wider uppercase">
                Rôle
              </th>
              <th className="text-dark-blue/50 px-4 py-3 text-left text-xs font-semibold tracking-wider uppercase">
                Qui le déclenche ?
              </th>
              <th className="text-dark-blue/50 px-4 py-3 text-left text-xs font-semibold tracking-wider uppercase">
                Utilité métier
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((statut, index) => (
              <tr
                key={`${title}-${statut.nom}`}
                className={`border-dark-blue/5 border-b ${index % 2 === 0 ? '' : 'bg-dark-blue/1.5'}`}
              >
                <td className="text-orange font-roboto-medium px-4 py-3 align-middle text-sm">
                  {statut.nom}
                </td>
                <td className="text-dark-blue/70 px-4 py-3 align-middle text-sm">{statut.role}</td>
                <td className="text-dark-blue/70 px-4 py-3 align-middle text-sm">
                  {statut.declencheur}
                </td>
                <td className="text-dark-blue/70 px-4 py-3 align-middle text-sm">
                  {statut.utilite}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="flex flex-col gap-3 md:hidden">
        {items.map((statut) => (
          <div
            key={`${title}-mobile-${statut.nom}`}
            className="border-orange/25 rounded-xl border-l-4 p-4"
          >
            <div className="text-orange font-roboto-medium mb-2">{statut.nom}</div>
            <div className="text-dark-blue/60 mb-1 text-sm">
              <span className="text-dark-blue/80 font-semibold">Rôle :</span> {statut.role}
            </div>
            <div className="text-dark-blue/60 mb-1 text-sm">
              <span className="text-dark-blue/80 font-semibold">Qui le déclenche ?</span>{' '}
              {statut.declencheur}
            </div>
            <div className="text-dark-blue/60 text-sm">
              <span className="text-dark-blue/80 font-semibold">Utilité métier :</span>{' '}
              {statut.utilite}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function FacturationElectronique() {
  const [activeSection, setActiveSection] = useState('contexte');

  const refs = {
    section: useRef<HTMLDivElement>(null),
    tag: useRef<HTMLDivElement>(null),
    title: useRef<HTMLHeadingElement>(null),
    subtitle: useRef<HTMLParagraphElement>(null),
  };

  // ─── Animation hero ───────────────────────────────────────────────────────
  useGSAP(
    () => {
      if (!refs.section.current || !refs.tag.current || !refs.title.current) return;

      const splitTitle = new SplitText(refs.title.current, { mask: 'lines', type: 'lines' });
      const splitSub = refs.subtitle.current
        ? new SplitText(refs.subtitle.current, { mask: 'lines', type: 'lines' })
        : null;

      gsap.set(refs.tag.current, { autoAlpha: 0, y: 14 });

      gsap
        .timeline({ defaults: { duration: MOTION.durationMd, ease: MOTION.ease } })
        .to(refs.tag.current, { autoAlpha: 1, y: 0, duration: MOTION.durationSm })
        .from(
          splitTitle.lines,
          { stagger: MOTION.staggerLines, yPercent: MOTION.yPercent },
          '-=0.1',
        )
        .from(
          splitSub?.lines ?? [],
          { stagger: MOTION.staggerLines, yPercent: MOTION.yPercent },
          '<+0.15',
        );
    },
    { scope: refs.section },
  );

  // ─── ScrollTrigger pour le sommaire actif ─────────────────────────────────
  useGSAP(() => {
    const sections = Array.from(document.querySelectorAll<HTMLElement>('section[id]'));
    if (!sections.length) return;

    const triggers = sections.map((section) =>
      ScrollTrigger.create({
        trigger: section,
        start: 'top 250px',
        end: 'bottom center',
        onEnter: () => setActiveSection(section.id),
        onEnterBack: () => setActiveSection(section.id),
      }),
    );

    ScrollTrigger.refresh();

    return () => {
      triggers.forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <div ref={refs.section}>
      <div className="max-w-custom mx-auto px-[clamp(20px,5vw,100px)] py-20">
        {/* ─── Hero ────────────────────────────────────────────────────────── */}
        <div className="grid w-full grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <div className="flex flex-col gap-5">
            <Tag ref={refs.tag}>Facturation électronique</Tag>

            <h1 ref={refs.title}>
              La Facture Électronique Obligatoire : Ce qu&apos;il faut savoir
            </h1>

            <p ref={refs.subtitle} className="text-dark-blue/70">
              Tout ce que vous devez savoir sur la future réforme fiscale liée à la facturation
              électronique en France : quels changements attendre et comment s'y préparer.
            </p>
          </div>

          <Image
            alt="illustration du logiciel"
            className="w-full rounded-(--radius-current)"
            height={600}
            src={withBasePath('/images/features/features10.webp')}
            width={900}
          />
        </div>

        {/* ─── Contenu + Sommaire ───────────────────────────────────────────── */}
        <div className="border-dark-blue/[0.07] mt-24 flex w-full gap-12 border-t pt-12">
          {/* Aside */}
          <aside className="hidden lg:block">
            <div className="border-dark-blue/7 bg-orange/2 sticky top-32 left-0 h-fit w-60 rounded-2xl border px-6 py-5">
              <p className="text-dark-blue font-roboto-medium mb-4 text-xs uppercase">Sommaire</p>
              <ul className="flex flex-col">
                {SUMMARY_ITEMS.map((item) => (
                  <li key={item.href}>
                    <SummaryLink
                      active={activeSection === item.href.slice(1)}
                      href={item.href}
                      label={item.label}
                    />
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Sections */}
          <div className="w-full min-w-0">
            <section className="scroll-mt-32 pb-16" id="contexte">
              <h2 className="mb-5">
                <UnderlinedText>Contexte</UnderlinedText>
              </h2>
              <p className="text-dark-blue/70 leading-relaxed">
                Dès 2026, la réforme sur la facturation électronique entre en vigueur pour toutes
                les entreprises françaises.
                <br />
                Conformément à l'article 289 du Code général des impôts, la facture électronique est
                un document entièrement numérique, couvrant toutes les étapes de son cycle de vie,
                de l'émission à la réception. Elle doit respecter un format et un mode de
                transmission définis par l'administration fiscale, afin de permettre un traitement
                automatisé et sécurisé. Les informations obligatoires d'une facture classique
                restent exigées, assurant ainsi la valeur juridique du document.
                <br />
                Objectif : moderniser, sécuriser et automatiser les échanges de factures dans un
                cadre réglementaire strict.
              </p>
            </section>

            <section className="scroll-mt-32 pb-16" id="definition">
              <h2 className="mb-5">
                La facture électronique <UnderlinedText>c&apos;est quoi au juste ?</UnderlinedText>
              </h2>
              <p className="text-dark-blue/70 mb-5 leading-relaxed">
                Une <span className="font-bold">facture électronique</span> est une facture créée,
                transmise, reçue et archivée{' '}
                <span className="font-bold">de manière entièrement numérique</span>, sans
                intervention papier, dont les données respectent une structure particulière et un
                mode de transmission normé par l&apos;administration fiscale, permettant un
                traitement automatisé et électronique.
              </p>
              <p className="text-dark-blue/70 mb-5 leading-relaxed">
                Il existe <span className="font-bold">deux formats officiels</span> autorisés :
              </p>
              <ul className="flex flex-col gap-3">
                <BulletItem>
                  <span className="font-bold">Factur-X</span> : format hybride contenant à la fois
                  un PDF lisible et des données XML lisibles par les systèmes. Ce format a pour
                  particularité, et avantage, d'être autant lisible par l'humain, que par
                  l'ordinateur et s'annonce comme{' '}
                  <span className="font-bold">le format le plus utilisé à l'avenir</span>. Il est
                  délivré via un fichier PDF (normé PDF/A-3) : celui-ci comprend des informations
                  lisibles ainsi que les données structurées et d'éventuelles pièces jointes.
                </BulletItem>
                <BulletItem>
                  <span className="font-bold">UBL ou CII</span> : formats structurés XML lisibles
                  uniquement par les logiciels mais non lisibles par l&apos;humain. Ces formats
                  seront vraisemblablement peu, voire pas utilisés par les TPE / PME.
                </BulletItem>
              </ul>
              <p className="text-dark-blue/70 my-5 leading-relaxed">
                Contrairement à un simple PDF envoyé par email, la facture électronique suit un{' '}
                <span className="font-bold">processus normé</span>, avec transmission via une{' '}
                <span className="font-bold">plateforme agréée</span> et un{' '}
                <span className="font-bold">suivi de statut réglementaire</span>.
              </p>
            </section>

            <section className="scroll-mt-32 pb-16" id="concernes">
              <h2 className="mb-5">
                <UnderlinedText>Qui est concerné</UnderlinedText> ?
              </h2>
              <p className="text-dark-blue/70 leading-relaxed">
                La réforme s&apos;applique à toutes les entreprises assujetties à la TVA, sans
                distinction de taille ou de secteur, y compris les cabinets d&apos;avocats. Elle
                concerne aussi bien les émetteurs que les destinataires de factures.
              </p>
            </section>

            <section className="scroll-mt-32 pb-16" id="reforme">
              <h2 className="mb-5">
                <UnderlinedText>Que prévoit la réforme</UnderlinedText> de la facture électronique ?
              </h2>
              <p className="text-dark-blue/70 mb-5 leading-relaxed">
                Elle vise à généraliser les échanges de factures électroniques entre entreprises
                assujetties à la TVA. Elle repose sur deux volets principaux à prendre en compte :
              </p>
              <ul className="flex flex-col gap-2">
                <BulletItem>E-invoicing pour les échanges B2B</BulletItem>
                <BulletItem>E-reporting pour les opérations B2C ou internationales</BulletItem>
              </ul>
              <p className="text-dark-blue/70 mt-5 leading-relaxed">
                Les échanges de factures électronique se font via une Plateforme Agrée (PA)
                connectée à zLawyer ou autre solution métier.
              </p>
            </section>

            <section className="scroll-mt-32 pb-16" id="plateforme-agre">
              <h2 className="mb-5">
                Qu&apos;est-ce qu&apos;une <UnderlinedText>Plateforme Agrée</UnderlinedText> ?
              </h2>
              <p className="text-dark-blue/70 mb-5 leading-relaxed">
                La <span className="font-bold">Plateforme Agréée</span> (anciennement appelée PDP –
                Plateforme de Dématérialisation Partenaire) constitue l'élément central du
                dispositif. Elle garantit la{' '}
                <span className="font-bold">conformité et la sécurité des échanges</span> de
                factures, tout en facilitant la gestion quotidienne des entreprises.
              </p>
              <p className="text-dark-blue/70 mb-5 leading-relaxed">
                Contrairement à un logiciel de facturation, la plateforme agréée est une
                infrastructure technique qui assure :
              </p>
              <ul className="flex flex-col gap-2">
                <BulletItem>
                  Le respect des formats normalisés pour les factures électroniques (UBL, CII,
                  Factur-X);
                </BulletItem>
                <BulletItem>
                  La circulation fluide et interopérable des factures entre toutes les plateformes
                  agréées, afin que chaque destinataire puisse les recevoir quelle que soit sa
                  solution ;
                </BulletItem>
                <BulletItem>
                  La transmission automatique à l'administration fiscale des données nécessaires au
                  suivi de la TVA et au e-reporting ;
                </BulletItem>
                <BulletItem>
                  L'archivage sécurisé des factures, garantissant leur intégrité et leur
                  accessibilité dans le temps.
                </BulletItem>
              </ul>
              <p className="text-dark-blue/70 mt-5 leading-relaxed">
                Toutes les plateformes agréées sont immatriculées par la DGFiP, qui veille à ce que
                chaque facture électronique soit émise, reçue et transmise conformément aux normes
                légales en vigueur.
              </p>
            </section>

            <section className="scroll-mt-32 pb-16" id="e-invoicing">
              <h2 className="mb-5">
                Qu&apos;est-ce que l&apos;<UnderlinedText>E-invoicing</UnderlinedText> ?
              </h2>
              <p className="text-dark-blue/70 mb-5 leading-relaxed">
                L&apos;<span className="font-bold">e-invoicing</span> s&apos;applique aux{' '}
                <span className="font-bold">transactions B2B domestiques</span> (entre entreprises
                françaises soumises à la TVA).
              </p>
              <p className="text-dark-blue/70 mb-5 leading-relaxed">
                Les factures doivent être émises et reçues via une{' '}
                <span className="font-bold">Plateforme Agréée Partenaire (PA)</span>, dans un{' '}
                <span className="font-bold">format électronique structuré ou hybride</span>.
              </p>
              <p className="text-dark-blue/70 mb-5 leading-relaxed">
                Les Plateformes de Dématérialisation Partenaire (PA) jouent donc un rôle central
                dans cette réforme en assurant la conformité et la sécurisation des échanges de
                factures électroniques.
              </p>
              <div className="border-orange/25 bg-orange/5 mt-5 rounded-xl border px-6 py-4">
                <p className="text-dark-blue/70 font-roboto-medium text-sm">
                  Important : l&apos;envoi de factures en simple PDF ne sera plus conforme à partir
                  de 2026.
                </p>
              </div>
              <p className="text-dark-blue/70 my-5 leading-relaxed">
                Assurez votre conformité à la réforme fiscale sur la facturation électronique en
                optant pour la solution de zLawyer.
                <br />
                Ainsi, vous bénéficiez d'une solution fluide et continue grâce à une interface
                unique.
              </p>
            </section>

            <section className="scroll-mt-32 pb-16" id="e-reporting">
              <h2 className="mb-5">
                Qu&apos;est-ce que l&apos;<UnderlinedText>E-reporting</UnderlinedText> ?
              </h2>
              <p className="text-dark-blue/70 mb-5 leading-relaxed">
                <span className="font-bold">E-reporting</span> : concerne{' '}
                <span className="font-bold">les transactions hors champ du e-invoicing</span>, mais
                pour lesquelles l'administration veut{' '}
                <span className="font-bold">suivre la TVA et les flux économiques</span>. On envoie{' '}
                <span className="font-bold">un relevé de transaction</span>, pas la facture.
              </p>
              <p className="text-dark-blue/70 mb-3 leading-relaxed">
                Les transactions concernées sont :
              </p>
              <ul className="flex flex-col gap-2">
                <BulletItem>
                  Transactions <span className="font-bold">B2C</span> (avec des particuliers).
                  <br />
                  Exemple : une entreprise française vend à un consommateur final en France ou à
                  l'étranger.
                </BulletItem>
                <BulletItem>
                  Transactions <span className="font-bold">B2B internationales.</span>
                </BulletItem>
                <BulletItem>
                  <span className="font-bold">Acquisitions intracommunautaires.</span>
                  <br />
                  Exemple : une entreprise française vend ou achète à une entreprise en Allemagne,
                  aux États-Unis, etc.
                </BulletItem>
                <BulletItem>
                  <span className="font-bold">Services rendus hors UE.</span>
                </BulletItem>
                <BulletItem>
                  Les <span className="font-bold">encaissements</span> soumis à TVA.
                </BulletItem>
              </ul>
              <p className="text-dark-blue/70 mt-5 leading-relaxed">
                L'entreprise envoie régulièrement (périodicité imposée : quotidien, hebdo ou mensuel
                selon les cas) des <span className="font-bold">données de transactions</span> à
                l'administration fiscale.
              </p>
              <div className="mt-4">
                <p className="text-dark-blue/70 leading-relaxed">Cela peut passer par :</p>
                <ul className="mt-3 flex flex-col gap-1.5 pl-4">
                  <li className="text-dark-blue/70 leading-relaxed">
                    <span className="bg-dark-blue/60 mt-1 mr-2 inline-block h-1.5 w-1.5 rounded-full" />
                    <span className="font-bold">Le Portail Public de Facturation (PPF)</span>;
                  </li>
                  <li className="text-dark-blue/70 leading-relaxed">
                    <span className="bg-dark-blue/60 mt-1 mr-2 inline-block h-1.5 w-1.5 rounded-full" />
                    Ou une <span className="font-bold">Plateforme Agrée (PA)</span> connectée à
                    zLawyer ou autre solution métier.
                  </li>
                </ul>
              </div>
              <p className="text-dark-blue/70 my-5 leading-relaxed">
                Pour les cabinets d'avocats, l'e-reporting via zLawyer permet de rester en
                conformité avec les nouvelles obligations fiscales tout en assurant la traçabilité
                et la transparence des opérations.
              </p>
              <p className="text-dark-blue/70 mb-5 leading-relaxed">
                <span className="font-bold">Le e-reporting suivra le même calendrier</span> que
                celui de la facture électronique, via leur plateforme.
              </p>
            </section>

            <section className="scroll-mt-32 pb-16" id="calendrier">
              <h2 className="mb-5">
                <UnderlinedText>Calendrier</UnderlinedText> de mise en œuvre
              </h2>
              <p className="text-dark-blue/70 mb-8 leading-relaxed">
                La réforme de la facturation électronique prévoit une mise en place progressive avec
                des échéances précises. Il est indispensable de respecter les délais légaux afin
                d'éviter toute sanction.
              </p>

              <div className="flex flex-col gap-0">
                {/* Phase 1 */}
                <div className="flex items-start gap-6">
                  <div className="border-orange/25 bg-orange/5 flex w-36 shrink-0 flex-col items-center rounded-xl border px-3 py-4 text-center">
                    <span className="text-dark-blue/40 text-xs">1er</span>
                    <span className="text-dark-blue font-roboto-medium text-sm">Septembre</span>
                    <span className="text-orange font-roboto-bold text-xl leading-none">2026</span>
                  </div>
                  <div className="pt-2">
                    <h4 className="text-dark-blue font-roboto-medium mb-2">Phase 1 :</h4>
                    <ul className="flex flex-col gap-1.5">
                      <BulletItem>
                        Obligation de{' '}
                        <span className="font-bold">
                          recevoir des factures électroniques pour TOUTES les entreprises
                        </span>
                      </BulletItem>
                      <BulletItem>
                        <span className="font-bold">Grandes entreprises et ETI</span> : Obligation
                        d&apos;émettre des factures électroniques
                      </BulletItem>
                    </ul>
                  </div>
                </div>

                {/* Connecteur */}
                <div className="border-orange/20 ml-[67px] h-8 w-px border-l-2 border-dashed" />

                {/* Phase 2 */}
                <div className="flex items-start gap-6">
                  <div className="border-orange/25 bg-orange/5 flex w-36 shrink-0 flex-col items-center rounded-xl border px-3 py-4 text-center">
                    <span className="text-dark-blue/40 text-xs">1er</span>
                    <span className="text-dark-blue font-roboto-medium text-sm">Septembre</span>
                    <span className="text-orange font-roboto-bold text-xl leading-none">2027</span>
                  </div>
                  <div className="pt-2">
                    <h4 className="text-dark-blue font-roboto-medium mb-2">
                      Phase 2 : PME, TPE et microentreprises
                    </h4>
                    <ul className="flex flex-col gap-1.5">
                      <BulletItem>
                        Obligation d&apos;émettre des factures électroniques pour les PME, TPE et
                        microentreprises
                      </BulletItem>
                      <BulletItem>
                        Généralisation complète de la facturation électronique
                      </BulletItem>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="border-orange/25 bg-orange/5 mt-8 rounded-xl border px-6 py-4">
                <p className="text-dark-blue/70 font-roboto-medium text-sm">
                  Préparez-vous dès maintenant pour être conforme à l&apos;échéance qui vous
                  concerne grâce à zLawyer.
                </p>
                <Button className="mt-3" href="/contact/" variant="transparent">
                  En savoir +
                </Button>
              </div>
            </section>

            <section className="scroll-mt-32 pb-16" id="obligations-des-entreprises">
              <h2 className="mb-5">
                <UnderlinedText>Obligations</UnderlinedText> des entreprises
              </h2>
              <ul className="flex flex-col gap-2">
                <BulletItem>
                  Utiliser une plateforme agréée (PA) pour l&apos;émission et la réception des
                  factures,
                </BulletItem>
                <BulletItem>Respecter les formats imposés (UBL, CII ou Factur-X),</BulletItem>
                <BulletItem>
                  Assurer la traçabilité, le cycle de vie et le reporting des factures.
                </BulletItem>
              </ul>
            </section>

            <section className="scroll-mt-32 pb-16" id="avantages-de-la-reforme">
              <h2 className="mb-5">
                <UnderlinedText>Avantages</UnderlinedText> de la réforme
              </h2>
              <ul className="flex flex-col gap-2">
                <BulletItem>
                  Réduction des délais de paiement, en facilitant le traitement et la validation des
                  factures,
                </BulletItem>
                <BulletItem>
                  Réduction des coûts liés au papier, à l'impression et à l'acheminement,
                </BulletItem>
                <BulletItem>Automatisation et fiabilisation des processus comptables,</BulletItem>
                <BulletItem>
                  Lutte contre la fraude à la TVA, en permettant un meilleur contrôle par
                  l'administration fiscale,
                </BulletItem>
                <BulletItem>
                  Renforcer la traçabilité et la transparence, grâce à des échanges numériques
                  sécurisés et horodatés,
                </BulletItem>
                <BulletItem>
                  Harmoniser les pratiques, notamment avec les obligations légales en France et en
                  Europe qui généralisent progressivement la facture électronique.
                </BulletItem>
              </ul>
            </section>

            <section className="scroll-mt-32 pb-16" id="mode-operatoire">
              <h2 className="mb-5">
                Le<UnderlinedText> mode opératoire</UnderlinedText>
              </h2>
              <div className="flex flex-col gap-6">
                <ul className="flex flex-col gap-2">
                  <li className="text-dark-blue/70 leading-relaxed">
                    <span className="text-orange mr-3 font-semibold">1.</span>
                    Création de la facture dans un outil conforme,
                  </li>
                  <li className="text-dark-blue/70 leading-relaxed">
                    <span className="text-orange mr-3 font-semibold">2.</span>
                    Transmission via une Plateforme Agrée (PA),
                  </li>
                  <li className="text-dark-blue/70 leading-relaxed">
                    <span className="text-orange mr-3 font-semibold">3.</span>
                    Réception et traitement automatisé de la facture par le client,
                  </li>
                  <li className="text-dark-blue/70 leading-relaxed">
                    <span className="text-orange mr-3 font-semibold">4.</span>
                    Suivi des statuts : déposée, rejetée, acceptée, payée…
                  </li>
                </ul>
                <Image
                  alt="Visuel de la Plateforme Agrée"
                  className="w-full max-w-[500px] rounded-(--radius-current)"
                  height={500}
                  src={withBasePath('/images/pa/Visuel-PA.webp')}
                  width={500}
                />
                <p className="text-dark-blue/70 leading-relaxed">
                  Pour cela, vous devez choisir une plateforme agrée comme zLawyer pour accéder à un
                  logiciel de facturation électronique accepté par l'administration fiscale.
                </p>
                <Button className="w-fit" href="/contact/" variant="transparent">
                  Je souhaite être contacté
                </Button>
              </div>
            </section>

            <section className="scroll-mt-32 pb-16" id="statuts">
              <h2 className="mb-5">
                Cycle <UnderlinedText>de vie et statuts</UnderlinedText>
              </h2>
              <p className="text-dark-blue/70 mb-8 leading-relaxed">
                Dans le cadre de la réforme, chaque facture électronique suit un cycle de vie
                standardisé, permettant de tracer toutes les étapes, de l'émission à l'archivage.
                Les statuts ci-dessous facilitent la conformité, la gestion et la traçabilité.
              </p>

              <StatusSection
                description="Voici les 4 statuts obligatoires à remonter à l'administration fiscale, tels que définis dans le cadre de la réforme de la facturation électronique :"
                items={STATUTS_OBLIGATOIRES}
                title="Statuts obligatoires"
              />
              <StatusSection
                description="Ces statuts ne sont pas obligatoires réglementairement, mais fortement recommandés pour faciliter le suivi des factures, l'automatisation et la qualité des échanges entre entreprises."
                items={STATUTS_RECOMMANDES}
                title="Statuts recommandés"
              />
              <StatusSection
                description="Ces statuts sont laissés à l'appréciation des acteurs pour enrichir la traçabilité. Ils ne sont pas remontés à la DGFiP, mais sont utiles pour les workflows internes et la gestion client-fournisseur."
                items={STATUTS_LIBRES}
                title="Statuts libres"
              />

              <div className="border-dark-blue/[0.07] mt-6 rounded-xl border px-6 py-4">
                <p className="text-dark-blue/60 font-roboto-medium text-sm">
                  Gestion centralisée de ces étapes = gain de temps et conformité garantie.
                </p>
              </div>
            </section>

            <section className="scroll-mt-32 pb-16" id="votre-partenaire-de-confiance">
              <h2 className="mb-5">
                zLawyer : <UnderlinedText>Votre partenaire de confiance</UnderlinedText>
              </h2>
              <p className="text-dark-blue/70 mb-6 leading-relaxed">
                Le logiciel zLawyer est conçu pour répondre aux exigences de la facturation
                électronique.
                <br />
                En optant pour zLawyer vous garantissez une totale conformité aux obligations
                découlant de la réforme sur la facturation électronique tout en bénéficiant
                d&apos;une solution performante et sécurisée.
              </p>
              <Button href="/facturation-electronique-et-zlawyer" variant="transparent">
                Notre solution à la réforme
              </Button>
            </section>

            {/* ─── CTA final ───────────────────────────────────────────────── */}
            <div className="border-dark-blue/[0.07] flex flex-col items-center gap-4 border-t pt-12 text-center">
              <h2>
                <UnderlinedText>Besoin</UnderlinedText> d&apos;en savoir plus ?
              </h2>
              <Button href="/contact/" variant="transparent">
                Je souhaite être contacté
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

FacturationElectronique.seo = FACTURATION_ELECTRONIQUE_SEO;
